/**
 * "A newer version may be available" (funding-statements.md, 2026-10-05).
 *
 * After every extraction the listener checks whether a settlement statement
 * (HUD / ALTA / Closing Disclosure) was uploaded AFTER the one the balance uses
 * — the locked statement's own source once locked — and the calculator carries
 * the hits as `hudNewerVersions[]`.
 *
 *   Use this one  → POST hud_source { documentId }               (409 when locked)
 *   Keep current  → POST hud_source { acknowledgeNewerVersions } (allowed when locked)
 *
 * Both clear the banner; an acknowledged document is never flagged again.
 * Renders nothing when the list is empty or absent.
 */
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import NewReleasesOutlined from "@mui/icons-material/NewReleasesOutlined";

import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { filePreviewSetData } from "../../../../../redux/reducers/filePreviewReducer";
import { Pill, T } from "../wireFundingShared";
import { settlementTypeLabel, useResolveLoanDocument } from "./HudSourceChip";

const NewerHudVersionsBanner = ({ versions, locked, busy, onUse, onKeep }) => {
  const dispatch = useDispatch();
  const resolveDoc = useResolveLoanDocument();
  const list = Array.isArray(versions) ? versions.filter(Boolean) : [];
  if (!list.length) return null;

  return (
    <Flx column gap={1}>
      {list.map((v) => {
        const doc = resolveDoc(v);
        const type = settlementTypeLabel(v);
        const name = v.fileName || v.fileStorageName || "a settlement statement";
        return (
          <Flx
            key={v.documentId}
            jb
            wrap
            gap={1.5}
            sx={{
              alignItems: "flex-start",
              px: 1.5,
              py: 1.25,
              borderRadius: T.radiusSm,
              bgcolor: T.amberTint,
              border: "1px solid #F2E2C4",
            }}
          >
            <Flx gap={1.25} sx={{ alignItems: "flex-start", minWidth: 0 }}>
              <NewReleasesOutlined
                sx={{ fontSize: 18, color: T.amber, mt: "1px", flex: "none" }}
              />
              <Flx column gap={0.4} sx={{ minWidth: 0 }}>
                <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
                  A newer version may be available:{" "}
                  <ButtonBase
                    onClick={() => doc && dispatch(filePreviewSetData(doc))}
                    disabled={!doc}
                    sx={{
                      font: "inherit",
                      color: T.accentInk,
                      verticalAlign: "baseline",
                      "&:hover": { textDecoration: "underline" },
                      "&.Mui-disabled": { color: T.ink2 },
                    }}
                  >
                    {name}
                  </ButtonBase>
                  {v.uploadDate
                    ? `, uploaded ${formatDateTime(v.uploadDate, "date")}`
                    : ""}
                </Txt>
                <Flx ac wrap gap={0.75}>
                  {type ? <Pill label={type} tone="info" /> : null}
                  {v.isExecuted === true ? (
                    <Pill label="Executed" tone="success" />
                  ) : v.isExecuted === false ? (
                    <Pill label="Draft" tone="warning" />
                  ) : null}
                  {v.sourceFileName ? (
                    <Txt sx={{ fontSize: 11.5, color: T.muted }}>
                      Newer than {v.sourceFileName}, which the balance uses
                    </Txt>
                  ) : null}
                </Flx>
              </Flx>
            </Flx>
            <Flx ac gap={1} sx={{ flex: "none" }}>
              <Button
                size="small"
                variant="text"
                disabled={!doc}
                onClick={() => doc && dispatch(filePreviewSetData(doc))}
              >
                View
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={onKeep}
              >
                Keep current
              </Button>
              <Tooltip
                title={
                  locked
                    ? "The funding statement is locked — unlock it to change the HUD source."
                    : ""
                }
              >
                <Box component="span">
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy || locked}
                    onClick={() => onUse(v.documentId)}
                  >
                    Use this one
                  </Button>
                </Box>
              </Tooltip>
            </Flx>
          </Flx>
        );
      })}
    </Flx>
  );
};

NewerHudVersionsBanner.propTypes = {
  versions: PropTypes.arrayOf(PropTypes.object),
  locked: PropTypes.bool,
  busy: PropTypes.bool,
  onUse: PropTypes.func.isRequired,
  onKeep: PropTypes.func.isRequired,
};

export default NewerHudVersionsBanner;
